import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Icon, IconButton, Tooltip, Button, Chip } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { DataGrid } from '@material-ui/data-grid';

import Loader from '../../common/loader';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { get } from '../../utils/api-services';
import themeColors from '../../assets/colors';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginBottom: 20
    },
    create: {
        marginLeft: 20,
        ...themeColors.greenBtn
    },

    tableContainer: {
        width: '100%'
    }
}));

const PolicyIndex = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const [state, setState] = useState({
        loading: true,
        data: [],
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'Policies | IAM';

        makeRequest();
    }, []);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/iamPolicy/index');
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                data: response?.data ?? [],
                roles: response?.roles
            }));
        } else {
            console.log('[PolicyIndex] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const valueGetter = ({ value }) => (value) ? 'CFG managed' : 'User managed';

    const renderName = ({ value, row }) => {
        return (
            <>
                {
                    row?.default &&
                    <Icon fontSize='inherit' style={{ marginRight: 8 }}>{'key_vertical'}</Icon>
                }
                <span>{` ${value}`}</span>
            </>
        );
    }

    const renderEffect = ({ value }) => <Chip size='small' style={{ backgroundColor: themeColors.visible[value] }} icon={<Icon fontSize='small'>{value ? 'check' : 'close'}</Icon>} label={value ? 'Allow' : 'Deny'} />;

    const renderCell = ({ row }) => {
        return (
            <Link to={`/iam/policies/detail/${row.idiam_policy}`} target='_blank'>
                <Tooltip title='Show detail' placement='top'>
                    <IconButton>
                        <Icon>{'visibility'}</Icon>
                    </IconButton>
                </Tooltip>
            </Link>
        );
    }

    const columns = [
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
        { field: 'default', headerName: 'Type', flex: 1, minWidth: 200, valueGetter, renderCell: renderName },
        { field: 'allow', headerName: 'Effect', flex: 0.5, minWidth: 100, renderCell: renderEffect },
        { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
        { field: 'actions', headerName: 'Actions', flex: 0.5, minWidth: 100, renderCell }
    ];

    if (state.loading) return <Loader />;

    return (
        <div className={classes.root}>
            <div className={classes.controls}>
                <Button
                    variant='contained'
                    className={classes.create}
                    component={Link}
                    to={'/iam/policies/create'}
                >
                    {'Create policy'}
                </Button>
            </div>

            <div className={classes.tableContainer}>
                <DataGrid
                    rows={state.data}
                    columns={columns}
                    pageSize={30}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idiam_policy}
                />
            </div>
        </div>
    );
}

export default PolicyIndex;